import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import ExplorePlans from "@/pages/ExplorePlans";
import CompanyDetail from "@/pages/CompanyDetail";
import PlanDetail from "@/pages/PlanDetail";
import ComparePlans from "@/pages/ComparePlans";
import QuotationGenerator from "@/pages/QuotationGenerator";
import SharedQuotation from "@/pages/SharedQuotation";
import AdvisorLogin from "@/pages/AdvisorLogin";
import AdvisorSetup from "@/pages/AdvisorSetup";
import AuthCallback from "@/pages/AuthCallback";
import AdvisorDashboard from "@/pages/AdvisorDashboard";
import AdvisorClients from "@/pages/AdvisorClients";
import AdvisorClientDetail from "@/pages/AdvisorClientDetail";
import AdvisorNewClient from "@/pages/AdvisorNewClient";
import AdvisorRenewals from "@/pages/AdvisorRenewals";
import MapPolicy from "@/pages/MapPolicy";
import RevenueDashboard from "@/pages/RevenueDashboard";
import BusinessDashboard from "@/pages/BusinessDashboard";
import LeadManagement from "@/pages/LeadManagement";
import LeadDetail from "@/pages/LeadDetail";
import EmployeeTasks from "@/pages/EmployeeTasks";
import QuoteGenerator from "@/pages/QuoteGenerator";
import QuoteView from "@/pages/QuoteView";
import PolicyholderRights from "@/pages/PolicyholderRights";
import PolicyXRayPage from "@/pages/PolicyXRayPage";
import ContestTracker from "@/pages/ContestTracker";
import VLITracker from "@/pages/VLITracker";
import HealthGrid from "@/pages/HealthGrid";
import MobileMorePage from "@/pages/MobileMorePage";
import ClaimDashboard from "@/pages/ClaimDashboard";
import DocumentLibrary from "@/pages/DocumentLibrary";
import ClientCommunication from "@/pages/ClientCommunication";
import PolicyResearch from "@/pages/PolicyResearch";
import ClientLogin from "@/pages/ClientLogin";
import ClientDashboard from "@/pages/ClientDashboard";
import ClientPolicyDetail from "@/pages/ClientPolicyDetail";
import TrainingAcademy from "@/pages/TrainingAcademy";
import TrainingModule from "@/pages/TrainingModule";
import Directory from "@/pages/Directory";
import TrainingChapter from "@/pages/TrainingChapter";
import TrainingQuiz from "@/pages/TrainingQuiz";
import XRayLeads from "@/pages/XRayLeads";
import WebsiteReviews from "@/pages/WebsiteReviews";
import TestimonialSubmit from "@/pages/TestimonialSubmit";
import TestimonialsAdmin from "@/pages/TestimonialsAdmin";
import DocumentCollection from "@/pages/DocumentCollection";
import DocSubmissionsAdmin from "@/pages/DocSubmissionsAdmin";
import QuotationRequests from "@/pages/QuotationRequests";
import ClaimDesk from "@/pages/ClaimDesk";
import DiwaliGift from "@/pages/DiwaliGift";
import ClaimHelp from "@/pages/ClaimHelp";
import PartnerLedger from "@/pages/PartnerLedger";
import DiwaliGiftsAdmin from "@/pages/DiwaliGiftsAdmin";
import ClaimDeskBar from "@/components/ClaimDeskBar";
import { initGA, trackPageView } from "@/utils/analytics";

// Initialize Google Analytics once
initGA();

// Component to track page views on route changes
function PageViewTracker() {
  const location = useLocation();
  
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  
  return null;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Component to handle auth callback detection synchronously
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render (NOT in useEffect)
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <PageViewTracker />
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/explore-plans" element={<ExplorePlans />} />
      <Route path="/compare" element={<ComparePlans />} />
      <Route path="/generate-quote" element={<QuotationGenerator />} />
      <Route path="/quote/:quoteId" element={<SharedQuotation />} />
      <Route path="/company/:companyId" element={<CompanyDetail />} />
      <Route path="/plan/:planId" element={<PlanDetail />} />
      <Route path="/plan/:planId/:section" element={<PlanDetail />} />
      <Route path="/policyholder-rights" element={<PolicyholderRights />} />
      <Route path="/policy-xray" element={<PolicyXRayPage />} />
      <Route path="/testimonial" element={<TestimonialSubmit />} />
      <Route path="/upload-documents" element={<DocumentCollection />} />
      <Route path="/claim-desk" element={<ClaimDesk />} />
      <Route path="/diwali-gift" element={<DiwaliGift />} />
      <Route path="/claim-help" element={<ClaimHelp />} />
      <Route path="/partner/:token" element={<PartnerLedger />} />
      {/* Advisor CRM Routes */}
      <Route path="/advisor/login" element={<AdvisorLogin />} />
      <Route path="/advisor/setup" element={<AdvisorSetup />} />
      <Route path="/advisor/auth/callback" element={<AuthCallback />} />
      <Route path="/advisor/dashboard" element={<AdvisorDashboard />} />
      <Route path="/advisor/clients" element={<AdvisorClients />} />
      <Route path="/advisor/clients/new" element={<AdvisorNewClient />} />
      <Route path="/advisor/clients/:clientId" element={<AdvisorClientDetail />} />
      <Route path="/advisor/renewals" element={<AdvisorRenewals />} />
      <Route path="/advisor/map-policy" element={<MapPolicy />} />
      <Route path="/advisor/revenue" element={<RevenueDashboard />} />
      <Route path="/advisor/business" element={<BusinessDashboard />} />
      <Route path="/advisor/leads" element={<LeadManagement />} />
      <Route path="/advisor/leads/:leadId" element={<LeadDetail />} />
      <Route path="/advisor/tasks" element={<EmployeeTasks />} />
      <Route path="/advisor/contests" element={<ContestTracker />} />
      <Route path="/advisor/vli" element={<VLITracker />} />
      <Route path="/advisor/health-grid" element={<HealthGrid />} />
      <Route path="/advisor/claims" element={<ClaimDashboard />} />
      <Route path="/advisor/documents" element={<DocumentLibrary />} />
      <Route path="/advisor/directory" element={<Directory />} />
      <Route path="/advisor/communication" element={<ClientCommunication />} />
      <Route path="/advisor/research" element={<PolicyResearch />} />
      <Route path="/advisor/quotes/new" element={<QuoteGenerator />} />
      <Route path="/advisor/quotes/:quoteId" element={<QuoteGenerator />} />
      <Route path="/quote/view/:quoteId" element={<QuoteView />} />
      <Route path="/advisor/more" element={<MobileMorePage />} />
      <Route path="/advisor/xray-leads" element={<XRayLeads />} />
      <Route path="/advisor/website-reviews" element={<WebsiteReviews />} />
      <Route path="/advisor/testimonials" element={<TestimonialsAdmin />} />
      <Route path="/advisor/diwali-gifts" element={<DiwaliGiftsAdmin />} />
      <Route path="/advisor/doc-submissions" element={<DocSubmissionsAdmin />} />
      <Route path="/advisor/quotation-requests" element={<QuotationRequests />} />
      {/* Training Academy Routes */}
      <Route path="/advisor/training" element={<TrainingAcademy />} />
      <Route path="/advisor/training/:moduleId" element={<TrainingModule />} />
      <Route path="/advisor/training/:moduleId/chapter/:chapterId" element={<TrainingChapter />} />
      <Route path="/advisor/training/:moduleId/quiz" element={<TrainingQuiz />} />
      {/* Client Portal Routes */}
      <Route path="/client/login" element={<ClientLogin />} />
      <Route path="/client/dashboard" element={<ClientDashboard />} />
      <Route path="/client/policy/:policyId" element={<ClientPolicyDetail />} />
      </Routes>
      <ClaimDeskBar />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-white">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors theme="dark" />
    </div>
  );
}

export default App;
